import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import useU2000AxiosAuthenticated from "../u2000Core/axios/U2000UseAxiosAuthenticated";
import { U2000HandleApiError } from "../u2000Core/axios/U2000HandleApiError";
import { useToast } from "../u2000Core/context/toast/U2000ToastContext";
import { NS } from "./U3125";
import {
  doForfaitWizardCommit,
  doForfaitWizardPreview,
  luHandleAppiError,
  parseForfaitWizardCommitLines,
  parseForfaitWizardPreviewLines,
  type U3125ForfaitWizardCommitLineResult,
  type U3125ForfaitWizardCommitRequest,
  type U3125ForfaitWizardPreviewLine,
} from "./U3125Service";

export interface U3125ForfaitWizardHandlersParams {
  onError?: (error: unknown) => void;
}

export function useU3125ForfaitWizardHandlers(params: U3125ForfaitWizardHandlersParams = {}) {
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();
  const { onError } = params;

  const handleApiError = useCallback(
    (error: unknown) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
      onError?.(error);
    },
    [t, toast, onError],
  );

  const preview = useCallback(
    async (projectNumbers: string[]): Promise<U3125ForfaitWizardPreviewLine[] | null> => {
      try {
        const res = await doForfaitWizardPreview(axios, { projectNumbers });
        return parseForfaitWizardPreviewLines(res.data);
      } catch (error: unknown) {
        handleApiError(error);
        return null;
      }
    },
    [axios, handleApiError],
  );

  const commit = useCallback(
    async (body: U3125ForfaitWizardCommitRequest): Promise<U3125ForfaitWizardCommitLineResult[] | null> => {
      try {
        const res = await doForfaitWizardCommit(axios, body);
        return parseForfaitWizardCommitLines(res.data);
      } catch (error: unknown) {
        handleApiError(error);
        return null;
      }
    },
    [axios, handleApiError],
  );

  return { preview, commit };
}
